import { Button } from '@/components/Button/Button';
import { OpcoUserStatus } from '@/bundle/_Opco/_OpcoUsersPage/ui/OpcoUserStatus/OpcoUserStatus';
import { StatusDetailsRow } from './styles';
import { DetailsRow } from '@/components/DetailsWidget/DetailsRow/DetailsRow';
import { formatDateTimeUTC } from '@/helpers/dateHelpers/dateHelpers';
import { ReelAnimateTime } from '@/bundle/shared/components/ReelAnimateTime/ReelAnimateTime';
import { Box } from '@/components/Box/Box';
import { UserDetailsRowTitle } from '@/bundle/_Opco/shared/styles';
import { useState } from 'react';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { CheckPinCodeModal } from '@/bundle/shared/components/CheckPinCodeModal/CheckPinCodeModal';
import {
  ORGANIZATION_USER_RESTRICTED_ACTION_ERROR_MESSAGE,
  PIN_TOKEN_EVENT_SCOPE,
  PinTokenEventScopeType,
} from '@/const/shared';
import { hasResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { CannotModifyUserErrorModal } from '@/bundle/shared/components/modals/CannotModifyUserErrorModal';
import { getOpcoOrganizationUsersPath } from '@/bundle/_Opco/ui/OrganizationDetails/path/path';
import { ToggleBlockUserConfirmModal } from '@/bundle/shared/components/modals/ToggleBlockUserConfirmModal';
import {
  isActiveOrganizationUser,
  isBlockedOrganizationStatus,
  isBlockedOrganizationUser,
  isInvitedOrganizationUser,
  isUnconfirmedOrganizationUser,
} from '@/helpers/organizationHelpers';
import { OrganizationUserType } from '@/bundle/_Opco/types/types';
import { useTimer } from '@/hooks/useTimer';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { UserLimitReachedModal } from '@/bundle/shared/components/UserLimitReachedModal/UserLimitReachedModal';
import { ORGANIZATION_USER_LIMIT_REACH_ERROR_MESSAGE } from '@/api/const';
import {
  blockOrganizationUser,
  reinviteOrganizationUser,
  unblockOrganizationUser,
} from '@/bundle/_Opco/ui/OrganizationDetails/Details/api/api';
import { loadOrganization } from '@/bundle/_Opco/api/api';

type OrganizationUserAccountDetailsStatusActionsType = {
  user: OrganizationUserType;
  onRefetchUser: () => void;
};

export const OrganizationUserAccountDetailsStatusActions = ({
  user,
  onRefetchUser,
}: OrganizationUserAccountDetailsStatusActionsType) => {
  const { organizationId, userId } = useParams<{ organizationId: string; userId: string }>();
  const navigate = useNavigate();
  const isBlockedUser = isBlockedOrganizationUser(user?.status);
  const [isPinCodeOpen, setIsPinCodeOpen] = useState(false);
  const [eventScope, setEventScope] = useState<PinTokenEventScopeType>(null);

  const [isToggleBlockUserModalOpen, setIsToggleBlockUserModalOpen] = useState(false);
  const [isResendInvitationOpen, setIsResendInvitationOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [openUsersLimitModal, setOpenUsersLimitModal] = useState(false);

  const closeToggleBlockUserModal = () => setIsToggleBlockUserModalOpen(false);
  const openToggleBlockUserModal = () => setIsToggleBlockUserModalOpen(true);

  const openResendInvitation = () => setIsResendInvitationOpen(true);
  const closeResendInvitation = () => setIsResendInvitationOpen(false);

  const openBlockUserPinCode = () => {
    setEventScope(PIN_TOKEN_EVENT_SCOPE.BLOCK_ORG_ADMIN);
    closeToggleBlockUserModal();
    setIsPinCodeOpen(true);
  };

  const openUnblockUserPinCode = () => {
    setEventScope(PIN_TOKEN_EVENT_SCOPE.UNBLOCK_ORG_ADMIN);
    closeToggleBlockUserModal();
    setIsPinCodeOpen(true);
  };

  const closePinCode = () => {
    setIsPinCodeOpen(false);
    setEventScope(null);
  };

  const { data: organizationData } = useQuery({
    queryKey: ['load_organization', organizationId],
    queryFn: () => loadOrganization(organizationId),
  });

  const { mutate: blockUserMutate } = useMutation({
    mutationKey: ['block_organization_user'],
    mutationFn: (pinCode: string) => {
      return blockOrganizationUser(organizationId, userId, pinCode);
    },
    onSuccess(response) {
      if (response?.error) {
        const hasError = hasResponseError(response?.error, ORGANIZATION_USER_RESTRICTED_ACTION_ERROR_MESSAGE);

        if (hasError) {
          return setIsErrorModalOpen(true);
        }

        return showSnackbarResponseError(response?.error);
      }

      showSnackbar('User account has been blocked.');
      onRefetchUser();
    },
  });

  const { mutate: unblockUserMutate } = useMutation({
    mutationKey: ['unblock_organization_user'],
    mutationFn: (pinCode: string) => {
      return unblockOrganizationUser(organizationId, userId, pinCode);
    },
    onSuccess(response) {
      if (response?.error) {
        const hasError = hasResponseError(response?.error, ORGANIZATION_USER_LIMIT_REACH_ERROR_MESSAGE);

        if (hasError) {
          return setOpenUsersLimitModal(true);
        }

        return showSnackbarResponseError(response?.error);
      }

      showSnackbar('User account has been unblocked.');
      onRefetchUser();
    },
  });

  const { mutate: reinviteOrganizationMutate } = useMutation({
    mutationKey: ['reinvite_organization_user'],
    mutationFn: () => {
      return reinviteOrganizationUser(organizationId, userId);
    },
    onSuccess(response) {
      if (response?.error) {
        onRefetchUser();
        closeResendInvitation();

        return showSnackbarResponseError(response?.error);
      }

      onRefetchUser();
      closeResendInvitation();
    },
  });

  const checkPinCodeSuccess = (pinToken: string) => {
    if (!eventScope) return;

    if (eventScope === PIN_TOKEN_EVENT_SCOPE.BLOCK_ORG_ADMIN) {
      blockUserMutate(pinToken);
      closePinCode();

      return;
    }

    if (eventScope === PIN_TOKEN_EVENT_SCOPE.UNBLOCK_ORG_ADMIN) {
      unblockUserMutate(pinToken);
      closePinCode();

      return;
    }
  };

  const { invitation_data, status } = user;
  const formattedLastInvitedDate = formatDateTimeUTC(invitation_data?.last_time_invited);
  const { seconds } = useTimer(invitation_data?.next_resend_available_in_sec, onRefetchUser);
  const timesInvited = invitation_data?.times_invited || '-';
  const isInvited = isInvitedOrganizationUser(status);
  const isUnconfirmed = isUnconfirmedOrganizationUser(status);
  const isActive = isActiveOrganizationUser(status);
  const isBlocked = isBlockedOrganizationUser(status);
  const isAvailableToModify = isActive || isInvited || isUnconfirmed;
  const isBlockedOrganization = isBlockedOrganizationStatus(organizationData?.body?.status);
  const isDisabledResendInvitation = !!seconds || isBlockedOrganization;

  return (
    <>
      <StatusDetailsRow>
        <Box display='flex' justifyContent='space-between'>
          <Box>
            <UserDetailsRowTitle>Status</UserDetailsRowTitle>
            {/* TODO: refactor this to own status component */}
            <OpcoUserStatus status={status} />
          </Box>

          <Box display='flex' columnGap='12px' alignItems='center'>
            {(isInvited || isUnconfirmed) && (
              <Button
                width={160}
                size='medium'
                variant='secondary'
                onClick={openResendInvitation}
                disabled={isDisabledResendInvitation}
              >
                {!seconds ? (
                  'Resend Invitation'
                ) : (
                  <>
                    Resend in &nbsp;
                    <ReelAnimateTime seconds={seconds} />
                  </>
                )}
              </Button>
            )}

            {isAvailableToModify && (
              <Button width={71} size='medium' variant='secondary' onClick={openToggleBlockUserModal}>
                Block
              </Button>
            )}

            {isBlocked && (
              <Button
                width={90}
                size='medium'
                variant='secondary'
                onClick={openToggleBlockUserModal}
                disabled={isBlockedOrganization}
              >
                Unblock
              </Button>
            )}
          </Box>
        </Box>

        {(isInvited || isUnconfirmed) && (
          <Box marginTop='20px'>
            <DetailsRow name='Last Invited'>{formattedLastInvitedDate}</DetailsRow>
            <DetailsRow name='Times Invited'>{timesInvited}</DetailsRow>
          </Box>
        )}
      </StatusDetailsRow>

      <ToggleBlockUserConfirmModal
        isOpen={isToggleBlockUserModalOpen}
        isBlockedUser={isBlockedUser}
        onClose={() => setIsToggleBlockUserModalOpen(false)}
        onConfirm={isBlockedUser ? openUnblockUserPinCode : openBlockUserPinCode}
      />

      <ConfirmModal
        isOpen={isResendInvitationOpen}
        header='Resend Invitation'
        body={
          <>
            A new invitation will be sent to the invited user. <br /> The previous one will be still valid.
          </>
        }
        onClose={closeResendInvitation}
        onConfirm={reinviteOrganizationMutate}
      />

      <CannotModifyUserErrorModal
        isOpen={isErrorModalOpen}
        header='User cannot be blocked'
        subHeader={
          <>
            Please assign another user of this organization <br /> as a Master Admin in order to block this user.
          </>
        }
        onClose={() => setIsErrorModalOpen(false)}
        onBack={() => navigate(getOpcoOrganizationUsersPath(organizationId))}
      />

      {isPinCodeOpen && (
        <CheckPinCodeModal
          isOpen={isPinCodeOpen}
          onSuccess={checkPinCodeSuccess}
          onClose={closePinCode}
          eventScope={eventScope}
        />
      )}
      <UserLimitReachedModal isOpen={openUsersLimitModal} onClose={() => setOpenUsersLimitModal(false)} />
    </>
  );
};
